import { defineMessages } from 'react-intl';

// the min number of stations to display in mystations and nowplaying.
export const NOW_PLAYING_RECENT_ITEMS_TO_DISPLAY = 5;
export const MY_STATIONS_STATIONS_TO_DISPLAY = 5;
export const StationConstants = Object.freeze({
  STATIONS_KEY: 'stations',
  STATION_HISTORY_KEY: 'stationHistory',
  CURRENT_STATION: 'currentStation',
  NEXT_STATION: 'nextStation',
  TOTAL_STATION_COUNT: 'TOTAL_STATION_COUNT',
  STATIONS_ERROR_KEY: 'error',
  SHUFFLE_STATION: 'shuffleStation',
  IS_IN_SHUFFLE_MODE: 'isInShuffleMode',
  IS_IN_SHUFFLE_ALL_MODE: 'isInShuffleAllMode',
  HAS_TAKEOVER_MODES: 'hasTakeoverModes',
  STATIONS_THAT_HAVE_RECEIVED_FRAGMENTS: 'stationsThatHaveReceivedFragments',
  AMG_ARTIST: 'ar',
  MUSIC_ID: 'mi',
  QUICKMIX: 'qm',
  SHARED: 'sh',
  MUSIC_ID_AC: 'mc',
  GIFTED: 'sg',
  AUTOPLAY_FACEBOOK: 'af',
  FACEBOOK_SUGGEST: 'fa',
  FRIENDS_STATION: 'fs',
  ARTIST_SUGGEST: 'as',
  GENRE_SUGGEST: 'gs',
  FACEBOOK_SUGGEST_SEARCH_VIEW: 'fv',
  RECOMMENDATION: 're',
  THUMBPRINT_RADIO: 'TT',
  THUMBPRINT_RADIO_KEY: 'TT0',
  STATION_TO_BE_REMOVED: 'stationToBeRemoved',
  SEO_TOKEN: 'st',
  IS_STATION_LIST_HOVERED: 'isStationListHovered',
  RECOMMENDATION_RESULTS: 'recommendationResults',
  SHOULD_SHORT_CIRCUIT_AFTER_GET_FRAGMENT: 'shouldShortCircuitAfterGetFragment',
  STATIONS_YOU_MAY_LIKE: 'StationsYouMayLike',
  IS_IN_EDIT_MODE: 'isInEditMode',
} as const);

/**
 * various types of stations that can be displayed in a user's station list.
 * The listener's stations, as well as suggested genres and artists.
 */
export const StationTypes = Object.freeze({
  LISTENER_STATION: 'LISTENER_STATION',
  GENRE_SUGGEST: 'GENRE_SUGGEST',
  ARTIST_SUGGEST: 'ARTIST_SUGGEST',
} as const);

export const StationMessages = defineMessages({
  listeners: {
    id: 'StationMessages.listeners',
    defaultMessage: `{listenerCount, plural,
      one {Listener}
      other {Listeners}
    }`,
  },

  monthlyListeners: {
    id: 'StationMessages.monthlyListeners',
    defaultMessage: `{listenerCount, plural,
      one {Monthly Listener}
      other {Monthly Listeners}
    }`,
  },

  listened: {
    id: 'StationMessages.listened',
    defaultMessage: 'Listened',
  },

  collected: {
    id: 'StationMessages.collected',
    defaultMessage: 'Collected',
  },

  collectedToday: {
    id: 'StationMessages.collectedToday',
    defaultMessage: 'Collected today',
  },

  listenedToday: {
    id: 'StationMessages.listenedToday',
    defaultMessage: 'Listened today',
  },

  collectedDaysAgo: {
    id: 'StationMessages.collectedDaysAgo',
    defaultMessage: `Collected {day, plural,
      one {# day ago}
      other {# days ago}
    }`,
  },

  listenedDaysAgo: {
    id: 'StationMessages.listenedDaysAgo',
    defaultMessage: `Listened {day, plural,
      one {# day ago}
      other {# days ago}
    }`,
  },

  collectedWeeksAgo: {
    id: 'StationMessages.collectedWeeksAgo',
    defaultMessage: `Collected {week, plural,
      one {# week ago}
      other {# weeks ago}
    }`,
  },

  listenedWeeksAgo: {
    id: 'StationMessages.listenedWeeksAgo',
    defaultMessage: `Listened {week, plural,
      one {# week ago}
      other {# weeks ago}
    }`,
  },

  collectedMonthsAgo: {
    id: 'StationMessages.collectedMonthsAgo',
    defaultMessage: `Collected {month, plural,
      one {# month ago}
      other {# months ago}
    }`,
  },

  listenedMonthsAgo: {
    id: 'StationMessages.listenedMonthsAgo',
    defaultMessage: `Listened {month, plural,
      one {# month ago}
      other {# months ago}
    }`,
  },

  collectedInMonthYear: {
    id: 'StationMessages.collectedInMonthYear',
    defaultMessage: 'Collected {month} {year}',
  },

  listenedInMonthYear: {
    id: 'StationMessages.listenedInMonthYear',
    defaultMessage: 'Listened {month} {year}',
  },

  neverCollected: {
    id: 'StationMessages.neverCollected',
    defaultMessage: 'Never collected',
  },

  neverListened: {
    id: 'StationMessages.neverListened',
    defaultMessage: 'Never listened',
  },

  nowPlaying: {
    id: 'StationMessages.nowPlaying',
    defaultMessage: 'Now Playing',
  },

  myStationsRecommendations: {
    id: 'StationMessages.myStationsRecommendations',
    defaultMessage: 'Stations You Might Like',
  },

  recommendedStations: {
    id: 'StationMessages.recommendedStations',
    defaultMessage: 'Recommended Stations For You',
  },

  sharedStation: {
    id: 'StationMessages.sharedStation',
    defaultMessage: 'SHARED STATION',
  },

  advertiserStation: {
    id: 'StationMessages.advertiserStation',
    defaultMessage: 'FEATURED STATION',
  },

  viewStations: {
    id: 'StatiosMessages.viewStations',
    defaultMessage: 'View Stations',
  },

  play: {
    id: 'StatiosMessages.play',
    defaultMessage: 'Play',
  },

  pause: {
    id: 'StationsMessages.pause',
    defaultMessage: 'Pause',
  },

  stationDetails: {
    id: 'StatiosMessages.stationDetails',
    defaultMessage: 'Station Details',
  },

  share: {
    id: 'StatiosMessages.share',
    defaultMessage: 'Share',
  },

  deleteMessage: {
    id: 'StatiosMessages.delete',
    defaultMessage: 'Delete',
  },

  select: {
    id: 'StatiosMessages.select',
    defaultMessage: 'Select a Station',
  },
} as const);

export const timeListenedStrings = defineMessages({
  daysListened: {
    id: 't.daysListened',
    defaultMessage: `{days, plural,
      one {1 day listened}
      other {# days listened}
    }`,
  },
  hoursListened: {
    id: 't.hoursListened',
    defaultMessage: `{hours, plural,
      one {1 hour listened}
      other {# hours listened}
    }`,
  },
  minutesListened: {
    id: 't.minutesListened',
    defaultMessage: `{minutes, plural,
      one {1 minute listened}
      other {# minutes listened}
    }`,
  },
  secondsListened: {
    id: 't.secondsListened',
    defaultMessage: `{seconds, plural,
      one {1 second listened}
      other {# seconds listened}
    }`,
  },
} as const);
